"use client";
import React from "react";
import { NavbarDemo } from "./NavBar";
import { motion } from "motion/react";
import { Heart, Users, Shield, Target, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function AboutUs() {
  const navigate = useNavigate();

  const coreValues = [
    {
      icon: Target,
      title: "AI-Powered Matching",
      description: "Intelligently connect clients with the best therapist using preferences, needs, and schedule."
    },
    {
      icon: Shield,
      title: "Optimize Occupancy",
      description: "No empty rooms, no wasted hours—maximize clinic efficiency and revenue."
    },
    {
      icon: Heart,
      title: "Therapist Flexibility",
      description: "Therapists set their own working hours, clients book directly and seamlessly."
    },
    {
      icon: Users,
      title: "Data-Driven Growth",
      description: "Transparent analytics and insights to increase profitability for all stakeholders."
    }
  ];

  const teamMembers = [
    {
      name: "Banti Shaw",
      role: "Founder & CEO",
      image: "/images/team/banti.jpg",
      quote: "Revolutionizing how people access therapy and wellness.",
      bio: "Banti Shaw is a serial entrepreneur with experience across wellness, technology, and real estate. As founder of Massage Quest, InspecUs, and now BookaTherapy, he has a track record of building solutions that simplify complex services and drive customer trust. His deep insight into the wellness industry comes from running successful clinics, where he witnessed the inefficiencies faced by therapists, clients, and clinic owners first-hand.",
      fullBio: [
        {
          heading: "Vision & Leadership",
          content: "With a problem-first mindset and proven execution, Banti is uniquely positioned to revolutionize the therapy and wellness market through AI-driven, scalable solutions. He understands the inefficiencies faced by therapists, clients, and clinic owners because he's experienced them firsthand while running successful wellness clinics."
        },
        {
          heading: "Track Record",
          content: "Banti has built multiple successful ventures: Massage Quest, a thriving therapeutic practice with strong client retention; InspecUs, recognized for professional home inspections integrating technology with compliance; and now BookaTherapy, designed to disrupt the traditional therapy market with AI-powered booking and modern clinic management."
        },
        {
          heading: "The BookaTherapy Mission",
          content: "BookaTherapy embodies Banti's vision of simplifying complex services through intelligent technology. By leveraging AI to match clients with the right therapist while giving clinics modern tools to optimize occupancy and increase revenue, he's solving real problems that impact thousands of therapists and clients daily."
        }
      ],
      featured: true
    },
    {
      name: "Adnan Patel",
      role: "Chief Technology Officer",
      image: "/images/team/adnan.jpg",
      quote: "Turning vision into reality through scalable, user-friendly technology.",
      bio: "Adnan Patel is a technologist and product builder with deep expertise in software development, cloud systems, and AI-driven platforms. As CTO of BookaTherapy, he leads the design of the company's secure, scalable, and user-friendly technology stack, including the AI-powered therapist matching engine and modern clinic management software.",
      fullBio: [
        {
          heading: "Technical Leadership",
          content: "With a background in backend development and cloud architecture, Adnan ensures BookaTherapy is built to scale and compliant with healthcare standards. He's responsible for the AI-powered matching system, secure infrastructure, and the intuitive clinic management platform that powers the business."
        },
        {
          heading: "The Visionary-Hacker Partnership",
          content: "Partnering with CEO Banti Shaw, Adnan brings the technical execution that turns vision into reality. While Banti identifies market gaps and opportunities, Adnan transforms those ideas into robust, scalable platforms that deliver exceptional user experiences."
        },
        {
          heading: "Building for the Future",
          content: "Adnan's focus is on creating technology that's both powerful and simple to use. From AI-driven matching algorithms to healthcare-compliant data systems, he ensures BookaTherapy's platform can handle growth while maintaining the highest standards of security and performance."
        }
      ],
      featured: true
    },
    {
      name: "Munira Patel",
      role: "Software Engineer",
      image: "/images/team/munira.jpg",
      bio: "Munira Patel is a talented software engineer specializing in frontend development and user interface design. She plays a crucial role in building intuitive, responsive interfaces that make BookaTherapy's platform accessible and easy to use for clients, therapists, and clinic administrators. With a keen eye for detail and a passion for creating seamless user experiences, Munira ensures that every interaction on the platform is smooth and efficient.",
      featured: false
    },
    {
      name: "Afzal Husen",
      role: "Software Engineer",
      image: "/images/team/afzal.jpg",
      bio: "Afzal Husen is an experienced software engineer focused on backend development and system architecture. He works on building scalable infrastructure, optimizing database performance, and implementing secure APIs that power BookaTherapy's booking engine. Afzal's expertise in cloud technologies and distributed systems ensures the platform can handle growing demand while maintaining reliability and speed.",
      featured: false
    },
    {
      name: "Nanda Jadhav",
      role: "Sales Expert",
      image: "/images/team/nanda.jpg",
      bio: "Nanda Jadhav is a seasoned sales professional with extensive experience in the wellness and healthcare industry. As BookaTherapy's Sales Expert, she leads client acquisition strategies, builds relationships with clinic partners, and drives business growth. Nanda's deep understanding of the market and her ability to connect with clients and partners make her instrumental in expanding BookaTherapy's reach and impact.",
      featured: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[var(--cloud)]">
      <NavbarDemo />

      {/* Header Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[var(--brand-navy)] via-[#1a2342] to-[var(--brand-navy)] py-24 px-6">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto text-center relative z-10"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-6"
          >
            <Sparkles className="w-5 h-5 text-[var(--brand-lime)]" />
            <span className="text-white/90 font-medium">About BookaTherapy</span>
          </motion.div>

          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight px-2">
            Revolutionizing
            <br />
            <span className="text-[var(--brand-lime)]">Massage Therapy Booking</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed px-2">
            Connecting clients with the perfect massage therapist through AI-powered matching, fair pricing, and instant availability.
          </p>
        </motion.div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[var(--brand-lime)] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--cloud)] via-white to-white" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--brand-lime)] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto relative z-10"
        >
          {/* Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[var(--brand-navy)] to-[var(--brand-lime)] rounded-full px-6 py-2 mb-6"
            >
              <Sparkles className="w-5 h-5 text-white" />
              <span className="text-white font-semibold">The Challenge</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--brand-navy)] mb-4 leading-tight">
              The Problem We're Solving
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Finding the right massage therapist shouldn't be <span className="text-[var(--brand-navy)] font-bold">frustrating</span> or <span className="text-[var(--brand-navy)] font-bold">expensive</span>
            </p>
          </div>

          {/* Problem Cards Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-3">Inconsistent Pricing</h3>
                <p className="text-gray-600 leading-relaxed">
                  Massage prices vary wildly from clinic to clinic, making it impossible to know if you're getting a <span className="font-bold text-blue-600">fair deal</span> or being overcharged.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-3">Wrong Therapist Match</h3>
                <p className="text-gray-600 leading-relaxed">
                  Booking a massage without knowing if the therapist's style matches your needs often leads to <span className="font-bold text-purple-600">disappointing experiences</span> and wasted money.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-500/10 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-3">Limited Availability</h3>
                <p className="text-gray-600 leading-relaxed">
                  Finding a therapist who's available when you need them is frustrating, often requiring <span className="font-bold text-teal-600">days of waiting</span> or settling for inconvenient times.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-3">No Loyalty Rewards</h3>
                <p className="text-gray-600 leading-relaxed">
                  Most massage services don't reward repeat clients, meaning you never get <span className="font-bold text-orange-600">recognized or rewarded</span> for your loyalty.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Solution Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-[var(--brand-lime)] to-[#c5e63d] rounded-3xl p-10 md:p-12 relative overflow-hidden shadow-2xl"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full opacity-10 -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full opacity-10 -ml-24 -mb-24" />
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-white rounded-full opacity-5 animate-pulse" />

            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-14 h-14 bg-[var(--brand-navy)] rounded-2xl flex items-center justify-center shadow-xl">
                  <Sparkles className="w-7 h-7 text-[var(--brand-lime)]" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-[var(--brand-navy)]">What We Offer</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4 bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all">
                  <div className="flex-shrink-0 w-12 h-12 bg-[var(--brand-navy)] rounded-xl flex items-center justify-center shadow-lg">
                    <Shield className="w-6 h-6 text-[var(--brand-lime)]" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[var(--brand-navy)] mb-2">Universal Pricing</h4>
                    <p className="text-[var(--brand-navy)]/90 leading-relaxed">
                      After almost a year of research, we've established fair, transparent pricing across all services. No surprises, no overcharging—just honest rates.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all">
                  <div className="flex-shrink-0 w-12 h-12 bg-[var(--brand-navy)] rounded-xl flex items-center justify-center shadow-lg">
                    <Users className="w-6 h-6 text-[var(--brand-lime)]" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[var(--brand-navy)] mb-2">Smart Persona Matching</h4>
                    <p className="text-[var(--brand-navy)]/90 leading-relaxed">
                      Our AI-powered system matches you with therapists based on your preferences, massage style, and desired outcomes—so you get the best fit every time.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all">
                  <div className="flex-shrink-0 w-12 h-12 bg-[var(--brand-navy)] rounded-xl flex items-center justify-center shadow-lg">
                    <Heart className="w-6 h-6 text-[var(--brand-lime)]" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[var(--brand-navy)] mb-2">Instant Availability</h4>
                    <p className="text-[var(--brand-navy)]/90 leading-relaxed">
                      With our large pool of qualified massage therapists, we can match you instantly with available professionals who fit your schedule.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all">
                  <div className="flex-shrink-0 w-12 h-12 bg-[var(--brand-navy)] rounded-xl flex items-center justify-center shadow-lg">
                    <Target className="w-6 h-6 text-[var(--brand-lime)]" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[var(--brand-navy)] mb-2">Loyalty Rewards</h4>
                    <p className="text-[var(--brand-navy)]/90 leading-relaxed">
                      Earn 1 point for every minute of massage. Collect 600 points and get a free 60-minute massage session—our way of saying thank you for being a loyal client.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[var(--cloud)] to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--brand-navy)] mb-4">How We Do It</h2>
            <p className="text-lg text-gray-600">The key features that power our platform</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  <div className="bg-gradient-to-br from-[var(--brand-navy)] to-[var(--brand-lime)] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--brand-navy)] mb-4">Why Now?</h2>
            <p className="text-lg text-gray-600">The perfect moment for transformation in wellness</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all border-l-4 border-[var(--brand-lime)]"
            >
              <div className="w-16 h-16 bg-[var(--brand-lime)]/20 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-[var(--brand-navy)]" />
              </div>
              <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-4">Post-Pandemic Health Focus</h3>
              <p className="text-gray-700 leading-relaxed">
                The pandemic shifted priorities—mental health and wellness are now top concerns for millions of people seeking proactive care and support.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all border-l-4 border-[var(--brand-lime)]"
            >
              <div className="w-16 h-16 bg-[var(--brand-lime)]/20 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-[var(--brand-navy)]" />
              </div>
              <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-4">Wellness Demand Rising</h3>
              <p className="text-gray-700 leading-relaxed">
                The wellness industry is experiencing unprecedented growth, with therapy and massage services seeing 5-7% annual growth rates.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all border-l-4 border-[var(--brand-lime)]"
            >
              <div className="w-16 h-16 bg-[var(--brand-lime)]/20 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[var(--brand-navy)]" />
              </div>
              <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-4">Digital Adoption Booming</h3>
              <p className="text-gray-700 leading-relaxed">
                Consumers now expect seamless digital experiences for booking, payments, and service discovery—making now the ideal time for platform innovation.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all border-l-4 border-[var(--brand-lime)]"
            >
              <div className="w-16 h-16 bg-[var(--brand-lime)]/20 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-[var(--brand-navy)]" />
              </div>
              <h3 className="text-2xl font-bold text-[var(--brand-navy)] mb-4">Clinics Still Old-School</h3>
              <p className="text-gray-700 leading-relaxed">
                Most therapy clinics still rely on outdated booking systems and manual processes, creating a massive opportunity for modern solutions.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--brand-navy)] mb-4">Meet Our Team</h2>
            <p className="text-lg text-gray-600">The people behind BookaTherapy</p>
          </motion.div>

          {/* Featured Leadership */}
          <div className="space-y-12 mb-16">
            {teamMembers.filter(m => m.featured).map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-3xl shadow-2xl overflow-hidden"
              >
                <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0`}>
                  {/* Image Section */}
                  <div className="lg:w-2/5 relative bg-gradient-to-br from-[var(--brand-navy)] to-[var(--brand-lime)] min-h-[400px] lg:min-h-[600px]">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-center p-8">
                        <div className="w-40 h-40 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 mx-auto">
                          <Users className="w-20 h-20 text-white" />
                        </div>
                        <p className="text-sm text-white/70 mb-2">Professional photo coming soon</p>
                        <p className="text-2xl font-bold">{member.name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="lg:w-3/5 p-8 md:p-12">
                    <div className="mb-6">
                      <h3 className="text-3xl md:text-4xl font-bold text-[var(--brand-navy)] mb-2">{member.name}</h3>
                      <p className="text-xl text-[var(--brand-lime)] font-semibold mb-4">{member.role}</p>
                      <div className="bg-[var(--cloud)] border-l-4 border-[var(--brand-lime)] p-4 rounded-r-lg italic text-gray-700 mb-6">
                        "{member.quote}"
                      </div>
                    </div>

                    <div className="space-y-6">
                      <p className="text-gray-700 leading-relaxed font-medium">{member.bio}</p>

                      {member.fullBio && member.fullBio.map((section, idx) => (
                        <div key={idx}>
                          <h4 className="text-lg font-bold text-[var(--brand-navy)] mb-2">{section.heading}</h4>
                          <p className="text-gray-600 leading-relaxed text-sm">{section.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Development Team - Smaller Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {teamMembers.filter(m => !m.featured).map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="relative bg-gradient-to-br from-[var(--brand-navy)] to-[var(--brand-lime)] p-8 text-center">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-12 h-12 text-white" />
                  </div>
                  <p className="text-xs text-white/70 mb-1">Professional photo coming soon</p>
                  <h3 className="text-xl font-bold text-white">{member.name}</h3>
                </div>
                <div className="p-6">
                  <p className="text-lg text-[var(--brand-lime)] font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-600 leading-relaxed text-sm">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[var(--brand-navy)] via-[#1a2342] to-[var(--brand-navy)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">Ready to Experience the Difference?</h2>
          <p className="text-xl text-white/80 mb-10 leading-relaxed">
            Join clients who've discovered better massage therapy through smart matching, fair pricing, and loyalty rewards.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/book')}
              className="bg-[var(--brand-lime)] text-[var(--brand-navy)] px-8 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all flex items-center gap-2 group"
            >
              Find Your Therapist
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/book')}
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold text-lg border-2 border-white/30 hover:bg-white/20 transition-all flex items-center gap-2"
            >
              Book Your Massage Now
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* Footer Spacer */}
      <div className="py-12 bg-white text-center text-gray-500">
        <p className="text-sm">© 2025 BookaTherapy. All rights reserved.</p>
      </div>
    </div>
  );
}
